import React from 'react';
import {
  Box, Typography, Grid, TextField, Button, IconButton, Chip, FormControl, InputLabel, Select, MenuItem, FormHelperText
} from '@mui/material';
import { Field, FieldArray, useFormikContext } from 'formik';
import { Icon } from '@iconify/react';

const SYNC_MODE_OPTIONS = [
  { value: 'polling', label: 'Periodic Polling' },
  { value: 'webhook', label: 'Real Time (Web hooks)' },
];
const ENDPOINT_IDENTIFIER_OPTIONS = [
  'locations', 'sessions', 'cdrs', 'tariffs', 'tokens', 'commands', 'versions', 'evses'
];
const OPERATOR_TYPE_OPTIONS = [
  { value: 'CPO', label: 'CPO' },
  { value: 'EMSP', label: 'EMSP' },
];

const TechnicalConfigStep = ({ errors = {}, touched = {} }) => {
  const { values, setFieldValue } = useFormikContext();

  // IP Whitelist add handler
  const handleAddIp = () => {
    if (values.ipInput && !errors.ipInput) {
      setFieldValue('ipWhitelist', [...(values.ipWhitelist || []), values.ipInput]);
      setFieldValue('ipInput', '');
    }
  };
  const handleRemoveIp = (idx) => {
    setFieldValue('ipWhitelist', values.ipWhitelist.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {/* Basic Configuration */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Basic Configuration
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Field name="baseUrl">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Base URL"
                fullWidth
                size="small"
                required
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={6}>
          <Field name="token">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Authentication Token"
                fullWidth
                size="small"
                required
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
              />
            )}
          </Field>
        </Grid>
      </Grid>

      {/* Security Configuration */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3, mt: 5 }}>
        Security Configuration
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Field name="clientCert">
            {({ field, meta }) => (
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 2 }}
                color={meta.touched && meta.error ? 'error' : 'primary'}
              >
                {field.value ? 'Client Certificate Uploaded' : 'Upload Client Certificate'}
                <input
                  type="file"
                  accept="application/x-pem-file,application/x-x509-ca-cert,application/pkix-cert,application/octet-stream,.crt,.pem,.cer"
                  hidden
                  onChange={e => setFieldValue('clientCert', e.currentTarget.files[0])}
                />
              </Button>
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={12}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={10}>
              <Field name="ipInput">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="IP Whitelist"
                    fullWidth
                    size="small"
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error}
                    margin="normal"
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={12} sm={2} marginTop="16px">
              <Button
                variant="contained"
                color="primary"
                sx={{ mb: { xs: 2, sm: 0 } }}
                onClick={handleAddIp}
                disabled={!values.ipInput || Boolean(errors.ipInput)}
                startIcon={<Icon icon="mdi:plus" width={20} height={20} />}
                fullWidth
              >
                Add
              </Button>
            </Grid>
            <Grid item xs={12}>
              {Array.isArray(values.ipWhitelist) && values.ipWhitelist.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {values.ipWhitelist.map((ip, idx) => (
                    <Chip
                      key={ip + idx}
                      label={ip}
                      onDelete={() => handleRemoveIp(idx)}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field name="requestsPerMinute">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Requests per Minute"
                type="number"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
                inputProps={{ min: 1 }}
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field name="burstLimit">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Burst Limit"
                type="number"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
                inputProps={{ min: 1 }}
              />
            )}
          </Field>
        </Grid>
      </Grid>

      {/* Sync Configuration */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3, mt: 5 }}>
        Sync Configuration
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Field name="syncMode">
            {({ field, meta }) => (
              <FormControl fullWidth size="small" error={meta.touched && Boolean(meta.error)} margin="normal">
                <InputLabel id="sync-mode-label">Sync Mode</InputLabel>
                <Select
                  {...field}
                  labelId="sync-mode-label"
                  label="Sync Mode"
                  value={field.value || ''}
                  onChange={e => setFieldValue('syncMode', e.target.value)}
                >
                  {SYNC_MODE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
              </FormControl>
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={6}>
          <Field name="syncInterval">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Sync Interval (Minutes)"
                type="number"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
                inputProps={{ min: 1 }}
              />
            )}
          </Field>
        </Grid>
      </Grid>

      {/* Endpoints */}
      {/* <Typography variant="h5" gutterBottom sx={{ mb: 3, mt: 5 }}>
        Endpoints
      </Typography>
      <FieldArray name="endpoints">
        {({ push, remove }) => (
          <>
            {values.endpoints && values.endpoints.length > 0 && values.endpoints.map((ep, idx) => (
              <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Field name={`endpoints[${idx}].identifier`}>
                      {({ field, meta }) => (
                        <FormControl fullWidth size="small" error={meta.touched && Boolean(meta.error)} margin="normal" required>
                          <InputLabel id={`endpoint-identifier-label-${idx}`}>Identifier</InputLabel>
                          <Select
                            {...field}
                            labelId={`endpoint-identifier-label-${idx}`}
                            label="Identifier *"
                            value={field.value || ''}
                            onChange={e => setFieldValue(`endpoints[${idx}].identifier`, e.target.value)}
                          >
                            {ENDPOINT_IDENTIFIER_OPTIONS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                          {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Field name={`endpoints[${idx}].operatorType`}>
                      {({ field, meta }) => (
                        <FormControl fullWidth size="small" error={meta.touched && Boolean(meta.error)} margin="normal" required>
                          <InputLabel id={`endpoint-operator-type-label-${idx}`}>Operator Type</InputLabel>
                          <Select
                            {...field}
                            labelId={`endpoint-operator-type-label-${idx}`}
                            label="Operator Type *"
                            value={field.value || ''}
                            onChange={e => setFieldValue(`endpoints[${idx}].operatorType`, e.target.value)}
                          >
                            {OPERATOR_TYPE_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field name={`endpoints[${idx}].endpointUrl`}>
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Endpoint URL"
                          fullWidth
                          size="small"
                          required
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          margin="normal"
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <IconButton color="error" onClick={() => remove(idx)} sx={{ mt: 1 }}>
                    <Icon icon="mdi:delete" width={20} height={20} />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              variant="contained"
              color="primary"
              startIcon={<Icon icon="mdi:plus" width={20} height={20} />}
              onClick={() => push({ identifier: '', operatorType: '', endpointUrl: '' })}
            >
              Add Endpoint
            </Button>
            {Array.isArray(errors.endpoints) && typeof touched.endpoints !== 'undefined' && typeof errors.endpoints === 'string' && (
              <FormHelperText error sx={{ mt: 1 }}>{errors.endpoints}</FormHelperText>
            )}
            {typeof errors.endpoints === 'string' && (!Array.isArray(errors.endpoints)) && (
              <FormHelperText error sx={{ mt: 1 }}>{errors.endpoints}</FormHelperText>
            )}
          </>
        )}
      </FieldArray> */}
    </Box>
  );
};

export default TechnicalConfigStep;
